// ManualReader renders the HSHM rule book markdown with a TOC sidebar
// and anchor-linked headings.
//
// Slug strategy: rehype-slug stamps every heading with a github-style
// id. parseHeadings (manualToc.js) uses the same github-slugger
// algorithm to predict those ids and builds the TOC. Both sides walk
// the headings in document order with a fresh slugger, producing
// byte-identical slugs — TOC anchors always point at a heading that
// exists.
//
// TOC entries are anchor links (`<a href="#slug">`). The browser
// handles scrolling natively when the URL hash changes; no JS
// scrollIntoView call needed for the in-document case. The
// route-mode caller (ManualPage) intercepts clicks via
// onNavigateSection to also update the route URL.
//
// Used by:
//   - ManualPage (full-page route at /manual and /manual/:slug)
//   - ManualDrawer (slide-over summoned from anywhere)

import React, { useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSlug from 'rehype-slug';
import remarkGfm from 'remark-gfm';

import { parseHeadings } from './manualToc';
import './ManualReader.css';


// Default react-markdown rehype set so we can append rehype-slug. We
// don't want to pull in any of the other defaults explicitly — just
// add slug on top.
const REHYPE_PLUGINS = [rehypeSlug];
const REMARK_PLUGINS = [remarkGfm];


function _scrollToSlug(container, slug) {
    if (!container || !slug) {
        return false;
    }
    const target = container.querySelector('[id="' + slug + '"]');
    if (!target) {
        return false;
    }
    target.scrollIntoView({ block: 'start' });
    return true;
}


function ManualReader(props) {
    const { markdown, initialSlug, onNavigateSection } = props;
    const bodyRef = useRef(null);
    const didInitialScrollRef = useRef(false);

    const headings = useMemo(
        () => parseHeadings(markdown || ''),
        [markdown]
    );

    function handleTocClick(event, slug) {
        // Always scroll the body container — anchor href="#slug"
        // would scroll the window, but our body is a separate
        // scrollable region inside the drawer/page. Prevent default
        // and drive the scroll ourselves.
        event.preventDefault();
        _scrollToSlug(bodyRef.current, slug);
        if (typeof onNavigateSection === 'function') {
            onNavigateSection(slug);
        }
    }

    useEffect(() => {
        if (didInitialScrollRef.current) {
            return;
        }
        if (!markdown) {
            return;
        }
        if (initialSlug) {
            const scrolled = _scrollToSlug(bodyRef.current, initialSlug);
            if (scrolled) {
                didInitialScrollRef.current = true;
            }
        } else {
            didInitialScrollRef.current = true;
        }
    }, [markdown, initialSlug]);

    if (!markdown) {
        return (
            <div className="manual-reader manual-reader-empty">
                Manual content is empty.
            </div>
        );
    }

    return (
        <div className="manual-reader">
            <nav className="manual-reader-toc" aria-label="Table of Contents">
                <div className="manual-reader-toc-title">Contents</div>
                <ul>
                    {headings.map((h) => (
                        <li
                            key={h.slug}
                            className={'manual-reader-toc-item '
                                + 'manual-reader-toc-depth-' + h.depth}
                        >
                            <a
                                href={'#' + h.slug}
                                onClick={(event) => handleTocClick(event, h.slug)}
                            >
                                {h.text}
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>
            <div className="manual-reader-body" ref={bodyRef}>
                <ReactMarkdown
                    remarkPlugins={REMARK_PLUGINS}
                    rehypePlugins={REHYPE_PLUGINS}
                >
                    {markdown}
                </ReactMarkdown>
            </div>
        </div>
    );
}

export default ManualReader;
